import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { QuotesTableOptionsComponent } from '../../shared/popover/quotes-table-options/quotes-table-options.component';
import { GlobalService } from '../../shared/services/global/global.service';
import { ItineraryService } from 'src/app/business-service/quote/itinerary.service';
import { ResponseNotificationService } from '../../shared/services/response-notification/response-notification.service';
import { StatusUpdateService } from 'src/app/business-service/status-update/status-update.service';
import { ComponentReloadService } from '../../shared/services/reload/component-reload.service';
import { ModalsService } from '../../shared/services/modals/modals.service';
import { PdfService } from '../../shared/services/pdf.service';
import { RECORD_STATUS_VALUES, TABLE_OPTION_VALUES } from '../../shared/constants/api-constants';
import { ColumnManageComponent } from '../../shared/popover/column-manage/column-manage.component';

@Component({
  selector: 'app-quotes-grid-view',
  templateUrl: './quotes-grid-view.component.html',
  styleUrls: ['./quotes-grid-view.component.scss'],
})
export class QuotesGridViewComponent implements OnInit {

  @Input() allData: any;
  @Input() data: any;
  @Input() hidePaginations: any = false;
  paginationSettings: any;
  visiblePages: number[] = [];
  noOfRows: any = 0;

  @Input() columns: any = [];
  @Input() showColumnSelector: boolean = true;
  initialColumns: any = [];

  @Input() hideExtraColumns: boolean = false;

  @Output() paginationsUpdated = new EventEmitter<void>();
  @Output() manageColumnsChange = new EventEmitter<any>();

  expandedRows: { [key: string]: boolean } = {};
  dataKeys: string[] = [];

  // Multi select related variables
  selectedRowIds: any[] = [];
  @Input() multiSelectProperty: string | null = null;

  @Output() emitRowOnCellClicked = new EventEmitter<any>();
  @Output() emitTableOptionClicked = new EventEmitter<any>();

  constructor(
    private _popOverController: PopoverController
  ) { }

  ngOnInit() {
    this.initialColumns = this.getVisibleColumns(this.columns);
  }

  ngOnChanges() {
    this.data = this.allData.data;

    this.paginationSettings = this.allData;


    if (this.data && Object.keys(this.data).length) {
      this.dataKeys = Object.keys(this.data);
      delete this.paginationSettings.data;
      this.noOfRows = this.data ? this.dataKeys.length : 0;
      this.updateVisiblePages();
    } else {
      this.dataKeys = [];
    }

    if (this.data && Object.keys(this.data).length === 0) {
      this.hidePaginations = true;
    } else {
      this.hidePaginations = false;
    }

  }

  toggleRow(key: string) {
    this.expandedRows[key] = !this.expandedRows[key];
  }

  // Options POP over

  async showOptions(ev: any, row: any) {
    const popover = await this._popOverController.create({
      component: QuotesTableOptionsComponent,
      event: ev,
      cssClass: 'popup-component',
      componentProps: {
        row: row,
        onPathSelected: (data: any) => this.onOptionSelected(data, row), // Ensure the function is passed correctly
      }
    });
    return await popover.present();
  }

  closeOptions() {
    this._popOverController.dismiss();
  }

  onOptionSelected(data: any, row: any) {
    this.emitTableOptionClicked.emit({ path : data.path, row: row });
    this.closeOptions();
  }

  onCellClicked(row: any, column: any) {
    this.emitRowOnCellClicked.emit({ row: row, column: column });
  }

  // Manage columns

  async openColumnOptions(ev: any) {
    const columnPopOver = await this._popOverController.create({
      component: ColumnManageComponent,
      event: ev,
      componentProps: { columns: this.cloneVariable(this.columns) },
      cssClass: "popup-component"
    })
    await columnPopOver.present();
    const { data } = await columnPopOver.onDidDismiss();
    if (data) {
      this.columns = data;
      this.initialColumns = data.filter((column: { showColumn: any; }) => column.showColumn);
      this.manageColumnsChange.emit(data);
    } else {
      this.resetColumn();
      this.ngOnInit();
    }
  }

  resetColumn() {
    this.initialColumns = this.columns.filter((column: { showColumn: any; }) => column.showColumn);
  }

  getVisibleColumns(columns: any) {
    return columns.filter((column: { showColumn: any; }) => column.showColumn);
  }

  shouldShowColumn(colKey: any): boolean {
    return this.initialColumns.some((column: any) => column.key === colKey && column.showColumn);
  }

  getfullColSpan() {
    let colspan = this.initialColumns.length
    if (this.showColumnSelector) {
      colspan = colspan + 1;
    }
    colspan = colspan + 1;
    return colspan;
  }

  cloneVariable(variable: any) {
    return JSON.parse(JSON.stringify(variable));
  }

  // Multi select variables

  // Helper to flatten all rows from grouped data
  private getAllRows(): any[] {
    if (!this.data || typeof this.data !== 'object') return [];
    return Object.values(this.data).flat();
  }

  // Check if all rows are selected
  isAllSelected(): boolean {
    if (!this.multiSelectProperty) return false;
    const allRows = this.getAllRows();
    if (allRows.length === 0) return false;
    return allRows.every(row => {
      const id = row?.[this.multiSelectProperty as string];
      return id !== undefined && id !== null && this.selectedRowIds.includes(id);
    });
  }

  // Toggle all rows
  toggleSelectAllRows(event: any) {
    if (!this.multiSelectProperty) return;
    const allRows = this.getAllRows();
    if (event.target.checked) {
      this.selectedRowIds = allRows
        .map(row => row?.[this.multiSelectProperty as string])
        .filter(id => id !== undefined && id !== null);
    } else {
      this.selectedRowIds = [];
    }
  }

  // Toggle single row
  toggleSelectRow(event: any, row: any) {
    if (!this.multiSelectProperty || !row) return;
    const id = row[this.multiSelectProperty as string];
    if (id === undefined || id === null) return;
    if (event.target.checked) {
      if (!this.selectedRowIds.includes(id)) {
        this.selectedRowIds.push(id);
      }
    } else {
      this.selectedRowIds = this.selectedRowIds.filter(selectedId => selectedId !== id);
    }
  }

  // Utility to check if a row is selected
  isRowSelected(row: any): boolean {
    if (!this.multiSelectProperty || !row) return false;
    const id = row[this.multiSelectProperty as string];
    return this.selectedRowIds.includes(id);
  }

  // Get all selected rows (from all groups)
  getSelectedRows(): any[] {
    if (!this.multiSelectProperty) return [];
    const allRows = this.getAllRows();
    return allRows.filter(row => {
      const id = row?.[this.multiSelectProperty as string];
      return id !== undefined && id !== null && this.selectedRowIds.includes(id);
    });
  }

  areRowsSelected(): boolean {
    return Array.isArray(this.selectedRowIds) && this.selectedRowIds.length > 0;
  }

  // Paginations
  updateVisiblePages() {
    const currentPage = this.paginationSettings.pageNumber;
    const totalPages = this.paginationSettings.totalPages;

    let startPage: number, endPage: number;
    if (totalPages <= 5) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPage <= 3) {
        startPage = 1;
        endPage = 5;
      } else if (currentPage + 2 >= totalPages) {
        startPage = totalPages - 4;
        endPage = totalPages;
      } else {
        startPage = currentPage - 2;
        endPage = currentPage + 2;
      }
    }

    this.visiblePages = Array.from(Array((endPage + 1) - startPage).keys()).map(i => startPage + i);
  }

  onPageChange(page: number) {
    if (page !== this.paginationSettings.pageNumber) {
      this.paginationSettings.pageNumber = page;
      this.updateVisiblePages();
      this.loadPageData();
    }
  }

  onNext() {
    if (this.paginationSettings.pageNumber < this.paginationSettings.totalPages) {
      this.paginationSettings.pageNumber++;
      this.updateVisiblePages();
      this.loadPageData();
    }
  }

  onPrevious() {
    if (this.paginationSettings.pageNumber > 1) {
      this.paginationSettings.pageNumber--;
      this.updateVisiblePages();
      this.loadPageData();
    }
  }

  onPageSizeChange() {
    this.paginationSettings.pageNumber = 1;
    this.updateVisiblePages();
    this.loadPageData();
  }

  loadPageData() {
    // Implement the function to load data for the current page
    this.paginationsUpdated.emit(this.paginationSettings);
  }

}
